import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@design-system';
import {
  QuickPlanPartnerDialog,
  QuickPlanPersonFields,
  quickPlanPersonSchema,
  useIntakeFormQuickPlan,
  type QuickPlanPersonValues,
} from '@/features/client-quick-plan';

import { z } from 'zod';
import { ArrowRight, UserPen, UserPlus, X } from 'lucide-react';

import { useEffect, useState } from 'react';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';

import { format } from 'date-fns';

interface IntakeFormQuickPlanFormProps {
  advisorId: string;
  onDone: () => void;
  onPartnerDialogChange?: (open: boolean) => void;
}

export function IntakeFormQuickPlanForm({
  advisorId,
  onDone,
  onPartnerDialogChange,
}: IntakeFormQuickPlanFormProps) {
  const { flags } = useFeatureFlags();
  const [showPartnerPanel, setShowPartnerPanel] = useState(false);
  const {
    form,
    hasPartner,
    canAddPartner,
    partnerLabel,
    handleTogglePartner,
    handleMaritalStatusChange,
    handlePartnerMaritalStatusChange,
    onSubmit,
    isValid,
    isPending,
  } = useIntakeFormQuickPlan({ advisorId, onSuccess: () => onDone() });

  const showPartnerOption = flags.coupleQuickPlan && canAddPartner;

  const partnerValues = form.watch('partner') as QuickPlanPersonValues | undefined;
  const partnerEmail = form.watch('partnerEmail') as string | undefined;
  const isPartnerValid = partnerValues
    ? quickPlanPersonSchema.safeParse(partnerValues).success &&
      z.string().email().safeParse(partnerEmail).success
    : false;

  useEffect(() => {
    onPartnerDialogChange?.(showPartnerPanel);
  }, [showPartnerPanel, onPartnerDialogChange]);

  function handleAddPartner() {
    handleTogglePartner(true);
    setShowPartnerPanel(true);
  }

  function handleRemovePartner() {
    handleTogglePartner(false);
    setShowPartnerPanel(false);
  }

  function handlePartnerDone() {
    setShowPartnerPanel(false);
  }

  function handlePartnerCancel() {
    if (isPartnerValid) {
      setShowPartnerPanel(false);
    } else {
      handleTogglePartner(false);
      setShowPartnerPanel(false);
    }
  }

  function handlePartnerDialogOpenChange(open: boolean) {
    if (!open) {
      handlePartnerCancel();
    } else {
      setShowPartnerPanel(true);
    }
  }

  const capitalizedLabel = partnerLabel.charAt(0).toUpperCase() + partnerLabel.slice(1);

  return (
    <Form {...form}>
      <form
        onSubmit={onSubmit}
        className="space-y-6"
      >
        <fieldset disabled={isPending}>
          <div className="space-y-6">
            <QuickPlanPersonFields
              control={form.control}
              onMaritalStatusChange={handleMaritalStatusChange}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Client email <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="jane@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </fieldset>

        {showPartnerOption && hasPartner && partnerValues && (
          <div className="border-border bg-muted flex items-start justify-between rounded-lg border p-3">
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 px-0.5">
              <p className="text-sm font-medium">
                {capitalizedLabel}: {partnerValues.firstName} {partnerValues.lastName}
              </p>
              <p className="text-muted-foreground text-sm">
                {partnerValues.birthDate ? format(partnerValues.birthDate, 'MMM d, yyyy') : ''} ·{' '}
                {partnerValues.sex === 'male' ? 'Male' : 'Female'} · {partnerValues.zipCode}
              </p>
            </div>
            <div className="flex shrink-0 gap-1">
              <Tooltip>
                <TooltipTrigger type="button">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 rounded-sm"
                    onClick={() => setShowPartnerPanel(true)}
                  >
                    <UserPen />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="px-2">Edit</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger type="button">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive size-7 rounded-sm"
                    onClick={handleRemovePartner}
                  >
                    <X />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="px-2">Remove</TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3">
          {showPartnerOption && !hasPartner && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={handleAddPartner}
            >
              <UserPlus />
              Add {partnerLabel}
            </Button>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={!isValid || isPending}
          >
            {isPending ? 'Sending...' : 'Send intake form'}
            {!isPending && <ArrowRight />}
          </Button>
        </div>

        <QuickPlanPartnerDialog
          open={showPartnerPanel}
          onOpenChange={handlePartnerDialogOpenChange}
          form={form}
          partnerLabel={partnerLabel}
          onMaritalStatusChange={handlePartnerMaritalStatusChange}
          onDone={handlePartnerDone}
          onCancel={handlePartnerCancel}
          isPartnerValid={isPartnerValid}
          extraFields={
            <FormField
              control={form.control}
              name="partnerEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {capitalizedLabel} email <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="partner@example.com"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          }
        />
      </form>
    </Form>
  );
}
